import React, { useState, useEffect } from "react";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/config";
import { fmtD } from "../crm/dateFormat";

// Docs tab of the Project Tracker (Owner only).
// Shows the reference notes that used to live under ABC: Logic, Roles, User Manual, Decisions.
// Same four collections and same fields as before. Nothing is copied, renamed or deleted by opening this tab.
const DOC_TABS = [
  { key: "logic", label: "Logic", col: "abcLogic" },
  { key: "roles", label: "Roles", col: "abcRoles" },
  { key: "manual", label: "User Manual", col: "abcUserManual" },
  { key: "decisions", label: "Decisions", col: "abcDecisions" },
];

const ts = (v) => (v && v.toMillis ? v.toMillis() : v ? new Date(v).getTime() || 0 : 0);
const fmt = (v) => {
  if (!v) return "";
  const d = v.toDate ? v.toDate() : new Date(v);
  return fmtD(d);
};
const body = (e) => (e.content != null ? e.content : e.details != null ? e.details : "");
const group = (e) => e.category || e.screen || "General";
const esc = (s) => String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const C = {
  card: { background: "#1a1d2e", border: "1px solid #2a2e45", borderRadius: 10, padding: "12px 14px", marginBottom: 10 },
  input: { width: "100%", boxSizing: "border-box", background: "#0f111a", border: "1px solid #3a3f5c", borderRadius: 8, padding: "8px 10px", color: "#e8eaf6", fontSize: 14 },
  btn: { background: "#2a2e45", color: "#e8eaf6", border: "1px solid #3a3f5c", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 13 },
  btnMain: { background: "#6c63ff", color: "#fff", border: "none", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  btnDel: { background: "#b03a2e", color: "#fff", border: "none", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 13 },
  tab: (on) => ({ padding: "7px 14px", borderRadius: 8, cursor: "pointer", fontSize: 13, border: "none", fontWeight: on ? 600 : 400, background: on ? "#6c63ff" : "#1a1d2e", color: on ? "#fff" : "#c8cce0" }),
};

export default function TrackerDocsTab() {
  const [active, setActive] = useState("logic");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(null); // null = closed, {id?, category, title, content}
  const [saving, setSaving] = useState(false);

  const tab = DOC_TABS.find((t) => t.key === active);

  const loadAll = async () => {
    setLoading(true);
    try {
      const out = {};
      for (const t of DOC_TABS) {
        const snap = await getDocs(collection(db, t.col));
        out[t.key] = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      }
      setData(out);
    } catch (e) {
      setMsg("Error loading the notes: " + e.message);
    }
    setLoading(false);
  };
  useEffect(() => { loadAll(); }, []);

  const entries = (data[active] || []).slice().sort((a, b) => group(a).localeCompare(group(b)) || ts(a.createdAt) - ts(b.createdAt));
  const words = search.toLowerCase().split(/\s+/).filter(Boolean);
  const visible = entries.filter((e) => {
    const hay = [e.title, e.name, group(e), body(e)].join(" ").toLowerCase();
    return words.every((w) => hay.includes(w));
  });
  const groups = {};
  visible.forEach((e) => { (groups[group(e)] = groups[group(e)] || []).push(e); });

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) { setMsg("Please fill Title and Content."); return; }
    if (!window.confirm("These are locked reference notes. Save this change?")) return;
    setSaving(true);
    try {
      const fields = { category: form.category.trim(), title: form.title.trim(), content: form.content };
      if (form.id) {
        await updateDoc(doc(db, tab.col, form.id), { ...fields, lastUpdatedAt: serverTimestamp() });
        setMsg("Note updated.");
      } else {
        await addDoc(collection(db, tab.col), { ...fields, createdAt: serverTimestamp() });
        setMsg("Note added.");
      }
      setForm(null);
      await loadAll();
    } catch (e) { setMsg("Save error: " + e.message); }
    setSaving(false);
  };

  const remove = async (e) => {
    if (!window.confirm("Delete this note for good? This cannot be undone.")) return;
    try {
      await deleteDoc(doc(db, tab.col, e.id));
      setMsg("Note deleted.");
      await loadAll();
    } catch (er) { setMsg("Delete error: " + er.message); }
  };

  const download = () => {
    let h = "<h2>" + esc(tab.label) + "</h2>";
    let last = null;
    entries.forEach((e) => {
      if (group(e) !== last) { last = group(e); h += "<h3>" + esc(last) + "</h3>"; }
      h += "<h4>" + esc(e.title || e.name || "(No title)") + "</h4><p>" + esc(body(e)).replace(/\n/g, "<br/>") + "</p><hr/>";
    });
    const html = "<html><head><meta charset='utf-8'><title>" + esc(tab.label) + "</title></head><body>" + h + "</body></html>";
    const url = URL.createObjectURL(new Blob(["\ufeff", html], { type: "application/msword" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "Docs_" + tab.key + "_" + new Date().toISOString().slice(0, 10) + ".doc";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div style={{ fontSize: 14, color: "#e8eaf6", marginBottom: 10 }}>
        <b>Docs</b> - reference notes for the apps: Logic, Roles, User Manual and Decisions. Only the Owner sees this tab.
      </div>
      {msg && <div style={{ ...C.card, color: "#c8cce0", fontSize: 13 }}>{msg}</div>}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
        {DOC_TABS.map((t) => (
          <button key={t.key} style={C.tab(active === t.key)} onClick={() => { setActive(t.key); setForm(null); setSearch(""); }}>
            {t.label} ({(data[t.key] || []).length})
          </button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <input style={{ ...C.input, flex: 1, minWidth: 200 }} placeholder="Search these notes" value={search} onChange={(e) => setSearch(e.target.value)} />
        <button style={C.btnMain} onClick={() => setForm({ category: "", title: "", content: "" })}>+ Add note</button>
        <button style={C.btn} onClick={download}>Download</button>
      </div>

      {form && (
        <div style={{ ...C.card, padding: 16 }}>
          <div style={{ fontWeight: 700, color: "#e8eaf6", marginBottom: 10 }}>{form.id ? "Edit note" : "New note in " + tab.label}</div>
          <label style={{ fontSize: 12, color: "#aab0c8" }}>Group (category)</label>
          <input style={{ ...C.input, marginBottom: 8 }} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          <label style={{ fontSize: 12, color: "#aab0c8" }}>Title</label>
          <input style={{ ...C.input, marginBottom: 8 }} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <label style={{ fontSize: 12, color: "#aab0c8" }}>Content</label>
          <textarea style={{ ...C.input, minHeight: 140, marginBottom: 10, fontFamily: "inherit" }} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
          <div style={{ display: "flex", gap: 8 }}>
            <button style={C.btnMain} disabled={saving} onClick={save}>{saving ? "Saving..." : "Save"}</button>
            <button style={C.btn} onClick={() => setForm(null)}>Cancel</button>
          </div>
        </div>
      )}

      {loading && <div style={{ color: "#7b82a0", fontSize: 13 }}>Loading...</div>}
      {!loading && visible.length === 0 && <div style={{ color: "#7b82a0", fontSize: 13 }}>No notes found.</div>}
      {Object.keys(groups).map((g) => (
        <div key={g}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#8b85ff", margin: "16px 0 8px", borderBottom: "1px solid #2a2e45", paddingBottom: 4 }}>{g}</div>
          {groups[g].map((e) => (
            <div key={e.id} style={C.card}>
              <div style={{ fontWeight: 700, color: "#fff", fontSize: 15 }}>{e.title || e.name || "(No title)"}</div>
              <div style={{ fontSize: 14, color: "#c8cce0", marginTop: 8, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{body(e)}</div>
              <div style={{ fontSize: 12, color: "#8d93ad", marginTop: 8 }}>
                Added: {fmt(e.createdAt)}{e.lastUpdatedAt ? " | Updated: " + fmt(e.lastUpdatedAt) : ""}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button style={C.btn} onClick={() => setForm({ id: e.id, category: e.category || "", title: e.title || e.name || "", content: body(e) })}>Edit</button>
                <button style={C.btnDel} onClick={() => remove(e)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}